import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { NetworkService } from './network.service';
import { StorageService } from './storage.service';
import AppConstants from '../constants/app.constants';
import { HttpService } from './http.service';

const CHAT_TRANSLATION_FALLBACKS: Record<string, Record<string, string>> = {
  en: {
    chat_typing_indicator_single: '{{name}} is typing...',
    chat_typing_indicator_double: '{{name1}} and {{name2}} are typing...',
    chat_typing_indicator_multiple: '{{name1}}, {{name2}} and {{count}} others are typing...',
  },
  de: {
    chat_typing_indicator_single: '{{name}} tippt...',
    chat_typing_indicator_double: '{{name1}} und {{name2}} tippen...',
    chat_typing_indicator_multiple: '{{name1}}, {{name2}} und {{count}} weitere tippen...',
  },
};

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  languages = AppConstants.LANGUAGES;
  selectedLanguage: any;
  public defaultLang = new BehaviorSubject(AppConstants.DEFAULT_LANGUAGE);

  constructor(
    private httpService: HttpService,
    private networkService: NetworkService,
    private storageService: StorageService,
  ) {
    this.loadStoredLanguage();
  }

  private loadStoredLanguage(): void {
    const storedLang = this.storageService.getItem(AppConstants.SELECTED_LANGUAGE);
    const langToUse = storedLang || AppConstants.DEFAULT_LANGUAGE;

    this.selectedLanguage = langToUse;
    this.defaultLang.next(langToUse);

    if (!storedLang) {
      this.storageService.setItem(AppConstants.SELECTED_LANGUAGE, langToUse);
    }
  }

  getTranslation(lang: string): Observable<any> {
    const cacheKey = `${AppConstants.TRANSLATION_PREFIX}${lang}`;
    const isOnline = this.networkService.networkStatus.value;
    const fallbackTranslations = this.getFallbackTranslations(lang);


    if (!isOnline) {
      const cached = this.storageService.getItem(cacheKey);

      return of({
        ...fallbackTranslations,
        ...(cached ? JSON.parse(cached) : {}),
      });
    }


    return this.httpService.postAuth('user/other/get-translations', { language: lang }).pipe(
      map((response: any) => {
        const data = response?.data || {};
        const mergedTranslations = {
          ...fallbackTranslations,
          ...data,
        };

        this.storageService.setItem(cacheKey, JSON.stringify(mergedTranslations));
        return mergedTranslations;
      }),
      catchError((error) => {
        console.error(
          'Translation API failed, falling back to localStorage:',
          error
        );
        const cached = this.storageService.getItem(cacheKey)
        return of({
          ...fallbackTranslations,
          ...(cached ? JSON.parse(cached) : {}),
        });
      })
    );
  }

  getSelectedLang(): string {
    return this.selectedLanguage;
  }

  getAllLanguage() {
    return this.languages;
  }

  setLanguage(lang: any) {
    this.defaultLang.next(lang);
    return this.defaultLang;
  }

  private getFallbackTranslations(lang: string): Record<string, string> {
    return CHAT_TRANSLATION_FALLBACKS[lang] || CHAT_TRANSLATION_FALLBACKS[AppConstants.DEFAULT_LANGUAGE] || {};
  }
}
